import { useQuery } from "@tanstack/react-query";
import api, { fmtOdds } from "../lib/api";
import { UpgradeGate } from "../components/Bits";
import { Zap, Radio, Fish } from "lucide-react";

const CAT_STYLE = {
  lock: "border-emerald-400/40 text-emerald-300",
  value: "border-cyan-400/40 text-cyan-300",
  longshot: "border-fuchsia-400/40 text-fuchsia-300",
  fish: "border-sky-400/40 text-sky-300",
};

function PickLive({ p }) {
  const live = p.live;
  const isHRBet = /hr|home run/i.test(p.bet || "");
  const cashedHR = isHRBet && live && live.hr > 0;
  return (
    <div className={`rounded-lg border p-3 ${cashedHR || p.result === "win" ? "border-[#22c55e]/50 bg-[#22c55e]/5 green-glow" : "border-white/10 bg-black/30"}`} data-testid={`livemode-pick-${p.id}`}>
      <div className="flex items-center gap-2">
        <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded border ${CAT_STYLE[p.category] || "border-white/15 text-neutral-400"}`}>{p.category}</span>
        <span className="font-semibold text-sm truncate flex-1">{p.player_name}</span>
        <span className="font-data text-xs text-neutral-400 shrink-0">{fmtOdds(p.odds)}</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs">
        <span className="text-neutral-500">{p.bet}</span>
        {live ? (
          p.pick_type === "pitcher" ? (
            <span className="font-data text-neutral-300">{live.p_k} Ks · {live.p_ip} IP</span>
          ) : (
            <span className="font-data text-neutral-300">{live.h}-{live.ab}{live.r > 0 ? ` · ${live.r} R` : ""}{live.rbi > 0 ? ` · ${live.rbi} RBI` : ""}</span>
          )
        ) : (
          <span className="text-neutral-600 font-data">{p.result !== "pending" ? p.result.toUpperCase() : "not started"}</span>
        )}
        {cashedHR && <span className="text-[10px] uppercase tracking-widest font-black text-[#22c55e] animate-pulse" data-testid={`livemode-gone-${p.id}`}>GONE! HR ✓</span>}
      </div>
    </div>
  );
}

export default function LiveHR() {
  const { data, isLoading } = useQuery({
    queryKey: ["liveHR"],
    queryFn: () => api.get("/live-hr").then((r) => r.data),
    refetchInterval: 120000,
  });
  const { data: liveMode } = useQuery({
    queryKey: ["liveMode"],
    queryFn: () => api.get("/live-mode").then((r) => r.data),
    refetchInterval: 120000,
  });
  const homers = data?.homers || [];
  const games = data?.games || [];
  const live = games.filter((g) => g.status === "In Progress");
  const picks = liveMode?.picks || [];

  return (
    <div className="space-y-5">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Live <span className="gold-text">Mode</span></h1>
        <p className="text-neutral-500 text-sm mt-1 flex items-center gap-2 flex-wrap">
          The whole slate, your board and every homer — one screen, updated every 2 minutes.
          {live.length > 0 && <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-red-400 border border-red-400/40 bg-red-500/10 px-2 py-0.5 rounded-full"><span className="live-dot" /> {live.length} Live</span>}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2 fade-up-1" data-testid="livemode-scoreboard">
        {games.map((g, i) => (
          <div key={i} className={`rounded-lg border p-2.5 ${g.status === "In Progress" ? "border-red-400/40 bg-red-500/5" : "border-white/10 bg-black/30"}`} data-testid={`scoreboard-game-${i}`}>
            <div className="text-[11px] text-neutral-300 truncate flex items-center gap-1.5">
              {g.status === "In Progress" && <span className="live-dot" style={{ width: 6, height: 6 }} />}{g.game}
            </div>
            <div className={`font-data text-sm font-bold mt-0.5 ${g.status === "In Progress" ? "text-red-400" : g.status === "Final" ? "text-neutral-400" : "text-[#a855f7]"}`}>
              {g.score || g.status}
            </div>
          </div>
        ))}
        {games.length === 0 && !isLoading && <p className="text-neutral-500 text-sm col-span-full">No games on today's slate.</p>}
      </div>

      <div className="grid lg:grid-cols-2 gap-5 fade-up-2">
        <div>
          <h2 className="font-display text-xl font-bold uppercase mb-1 flex items-center gap-2"><Radio size={16} className="text-[#a855f7]" /> Your Board — <span className="text-[#a855f7]">Live</span></h2>
          <div className="accent-bar w-32 mb-3" />
          <div className="space-y-2" data-testid="livemode-board">
            {picks.map((p) => <PickLive key={p.id} p={p} />)}
            {picks.length === 0 && (
              <div className="card-surface rounded-xl p-6 text-center text-sm text-neutral-500" data-testid="livemode-board-empty">
                No picks on today's board yet.
              </div>
            )}
            {liveMode?.locked && <UpgradeGate feature="Live tracking for the full board" />}
          </div>
        </div>

        <div>
          <h2 className="font-display text-xl font-bold uppercase mb-1 flex items-center gap-2"><Zap size={16} className="text-[#a855f7]" /> HR <span className="text-[#a855f7]">Feed</span></h2>
          <div className="accent-bar w-32 mb-3" />
          <div className="space-y-3">
            {isLoading && <div className="text-neutral-500 font-data text-sm animate-pulse">Checking the slate...</div>}
            {!isLoading && homers.length === 0 && (
              <div className="card-surface rounded-xl p-8 text-center" data-testid="livehr-empty">
                <Zap className="mx-auto text-neutral-700 mb-3" size={26} />
                <p className="text-neutral-400 text-sm">No homers yet today. First bombs usually land once games get going.</p>
              </div>
            )}
            {homers.map((h, i) => (
              <div key={i} className="card-surface hover:border-[#a855f7]/40 rounded-lg p-3.5 flex gap-3 transition-colors duration-200" data-testid={`livehr-item-${i}`}>
                <div className="w-9 h-9 rounded-full bg-[#a855f7]/15 border border-[#a855f7]/40 flex items-center justify-center shrink-0">
                  <Zap size={15} className="text-[#a855f7]" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white">{h.team} <span className="text-neutral-500 font-normal">· {h.inning} · {h.rbi} RBI</span></div>
                  <p className="text-xs text-neutral-400 mt-0.5">{h.description}</p>
                  <div className="text-[11px] text-neutral-600 mt-1">{h.game} · {h.status}</div>
                </div>
              </div>
            ))}
            {data?.locked && <UpgradeGate feature="The full live homer feed" />}
          </div>
        </div>
      </div>
    </div>
  );
}
